/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/ui/badge.js';
import { type Therapist } from '../../lib/api/resources.js';
import { formatInr } from '../../lib/utils/money.js';

const COLS = ['name', 'type', 'specialty', 'sessions', 'status', 'pay'] as const;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const v = `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase();
  return v === '' ? '?' : v;
}

function payLabel(t: Therapist): { amount: string; unit: string } | null {
  if (t.category === 'consultant') {
    return t.perSessionRatePaise === null
      ? null
      : { amount: formatInr(t.perSessionRatePaise), unit: 'perSession' };
  }
  return t.monthlyFixedPaise === null
    ? null
    : { amount: formatInr(t.monthlyFixedPaise), unit: 'perMonth' };
}

function TherapistRow({
  therapist,
  onSelect,
}: {
  readonly therapist: Therapist;
  readonly onSelect: (t: Therapist) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const pay = payLabel(therapist);
  const handleClick = useCallback(() => {
    onSelect(therapist);
  }, [onSelect, therapist]);
  return (
    <tr
      className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60"
      onClick={handleClick}
    >
      <td className="px-5 py-3">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-xs font-bold text-white">
            {initials(therapist.fullName)}
          </span>
          <div className="min-w-0">
            <p className="truncate font-medium text-slate-800 dark:text-white">
              {therapist.fullName}
            </p>
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">{therapist.email}</p>
          </div>
        </div>
      </td>
      <td className="px-5 py-3">
        <Badge
          label={t(`form.therapist.categoryOption.${therapist.category}`)}
          tone={therapist.category === 'consultant' ? 'violet' : 'slate'}
        />
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
        {therapist.specialisations.length > 0 ? therapist.specialisations.join(', ') : '—'}
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{therapist.sessionsCount}</td>
      <td className="px-5 py-3">
        <Badge
          label={t(`status.${therapist.status}`)}
          tone={therapist.status === 'active' ? 'green' : 'slate'}
        />
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
        {pay === null ? '—' : `${pay.amount}${t(`admin.therapists.${pay.unit}`)}`}
      </td>
    </tr>
  );
}

/** Admin → Therapists table (Phase 6 / Standalone-2 design). Row click → profile. */
export function TherapistTable({
  rows,
  onSelect,
}: {
  readonly rows: readonly Therapist[];
  readonly onSelect: (t: Therapist) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
          <tr>
            {COLS.map((c) => (
              <th key={c} className="px-5 py-3 font-semibold">
                {t(`admin.therapists.col.${c}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((therapist) => (
            <TherapistRow key={therapist.id} therapist={therapist} onSelect={onSelect} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
